<script setup lang="ts">
/**
 * GroupSection —— 首页 grid 区容器（F12，Q6/Q7 + 模板 `.scu-widget`）。
 *
 * 卡片型 type（problem / method / innovation）的 HomePage 外层壳：根
 * `<section>` 作为 HomePage grid 直接子项（grid item），`id=type` 供
 * AnchorNav `#type` 命中；标题 tab 渲染 `<Icon>` + `resolveGroupTitle`
 * 返回的 displayName；内部 `.scu-grid` 由默认 slot 填充单卡组件
 * （ProblemSection / MethodSection / InnovationSection，1 卡 = 1 Section）。
 *
 * **复用 `SectionCard` 壳**（DRY）：`title-icon` slot 放区图标，默认 slot
 * 放 `.scu-grid` 容器。区别于单 Section 用途——GroupSection 是"区"壳，
 * 不传 `summary` / `show-draft-badge`（区级无摘要，草稿角标 per card 由
 * 卡片组件经 GridItem 渲染），不填 `more` slot（「更多+」per card，非区级）。
 */
import SectionCard from './SectionCard.vue';
import DetailMoreLink from '@/components/DetailMoreLink.vue';
import type { IconName } from '@/components/icon-registry';
import type { PublicSectionView } from '@/api/types';

const props = defineProps<{
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly icon?: IconName;
  readonly moreSection?: PublicSectionView;
  readonly previewToken?: string | null;
}>();
</script>

<template>
  <SectionCard :id="id" :type="type" :title="title">
    <template v-if="props.moreSection" #more>
      <DetailMoreLink :section="props.moreSection" :preview-token="props.previewToken" />
    </template>
    <div
      :class="[$style.grid, { [$style.innovationGrid]: type === 'innovation' }]"
      data-shell="scu-grid"
    >
      <slot />
    </div>
  </SectionCard>
</template>

<style module>
/* 模板 .scu-grid：单列卡片纵向排列（PC 端区容器本身已占 HomePage grid 一列）。 */
.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--template-grid-gap);
}

.innovationGrid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 719px) {
  .innovationGrid {
    grid-template-columns: 1fr;
  }
}
</style>
